import React from "react";
import { MessagesConsumer, MessageTypes } from "../contexts/MessagesContext";
import { AuthConsumer } from "../contexts/AuthContext";
import axios from "axios";

export function WithApiAccess(Component) {
  const redirectTimer = (location) =>
    setTimeout(() => {
      window.location.replace(location);
    }, 7000);
  return class extends React.Component {
    createApi = (messagesContext, authContext) => {
      if (this.interceptorId !== undefined) {
        axios.interceptors.response.eject(this.interceptorId);
      }

      let isRefreshing = false;
      let failedQueue = [];

      const processQueue = (error, token = null) => {
        failedQueue.forEach((prom) => {
          if (error) {
            prom.reject(error);
          } else {
            prom.resolve(token);
          }
        });

        failedQueue = [];
      };

      this.interceptorId = axios.interceptors.response.use(
        function (response) {
          return response;
        },
        function (error) {
          const originalRequest = error.config;

          if (
            error.response.status === 401 &&
            originalRequest.url === "api/auth/refresh"
          ) {
            authContext.logout();
          }

          if (error.response.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
              return new Promise(function (resolve, reject) {
                failedQueue.push({ resolve, reject });
              })
                .then((token) => {
                  originalRequest.headers["Authorization"] = "Bearer " + token;
                  return axios(originalRequest);
                })
                .catch((err) => {
                  return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            return new Promise(function (resolve, reject) {
              axios
                .post("api/auth/refresh", {
                  accessToken: localStorage.accessToken,
                  refreshToken: localStorage.refreshToken,
                })
                .then((tokenRefreshResponse) => {
                  authContext.loggedIn(
                    tokenRefreshResponse.data.id,
                    tokenRefreshResponse.data.username,
                    tokenRefreshResponse.data.accessToken,
                    tokenRefreshResponse.data.refreshToken,
                    tokenRefreshResponse.data.claims,
                    () => {},
                  );
                  axios.defaults.headers.common["Authorization"] =
                    "Bearer " + tokenRefreshResponse.data.accessToken;
                  originalRequest.headers["Authorization"] =
                    "Bearer " + tokenRefreshResponse.data.accessToken;
                  processQueue(null, tokenRefreshResponse.data.accessToken);
                  resolve(axios(originalRequest));
                })
                .catch((err) => {
                  authContext.logout();
                  reject(err);
                })
                .then(() => {
                  isRefreshing = false;
                });
            });
          }

          return Promise.reject(error);
        },
      );
      ////////////////////////////////////

      const addSuccess = (message) => {
        messagesContext.addMessage(MessageTypes.Success, message);
      };

      const addError = (message) => {
        messagesContext.addMessage(MessageTypes.Error, message);
      };

      const addServerErrorMessage = () => {
        addError(
          "There was a problem with your request. Please try again later or contact support if the problem persists.",
        );
      };

      const handleResponse = (response) => {
        if (response.status === 200) {
          return response;
        } else if (response.ok) {
          return response;
        } else if (response.status === 401) {
          authContext.logout();
        } else if (response.status === 403) {
          window.location.replace("/unauthorized");
        } else if (response.status === 404) {
          addError(
            "The resource you are looking for does not exist or has been removed.",
          );
        } else if (response.status === 422) {
          if (response.headers["content-type"] && response.data.Message) {
            addError(response.data.Message);
          } else if (
            response.headers.get("Content-Type").startsWith("application/json")
          ) {
            return response.json().then((data) => {
              if ("message" in data) {
                addError(data.message);
              } else if ("Message" in data) {
                addError(data.Message);
              } else {
                addServerErrorMessage();
              }
            });
          } else {
            addServerErrorMessage();
          }
        } else if (response.status === 500) {
          addServerErrorMessage();
        } else {
          addServerErrorMessage();
        }
      };

      const buildEncodedApiUrl = (endpoint) => {
        var url = "";
        url += endpoint;
        let uri = encodeURI(url);
        return uri;
      };

      return {
        get: (endpoint) => {
          const headers = {
            Accept: "application/json; charset=utf-8",
            Authorization: "Bearer " + localStorage.accessToken,
          };
          return new Promise(async (resolve, reject) => {
            return axios
              .get(endpoint, { headers })
              .then((response) => resolve(response))
              .catch((error) => {
                if (error && error.response) {
                  handleResponse(error.response);
                }
                reject();
              });
          });
        },
        getContract: (endpoint) => {
          const headers = {
            Accept: "application/json; charset=utf-8",
            Authorization: "Bearer " + localStorage.accessToken,
          };
          return new Promise(async (resolve, reject) => {
            return axios
              .get(endpoint, { headers })
              .then((response) => resolve(response))
              .catch((error) => {
                if (
                  error &&
                  error.response &&
                  (error.response.status === 404 ||
                    error.response.status === 500)
                ) {
                  addError(
                    "The contract number you provided is either invalid or not active. You will now be redirected to the homepage.",
                  );
                  redirectTimer("/");
                } else if (error && error.response) {
                  handleResponse(error.response);
                }
                reject();
              });
          });
        },
        getContractDisbursementDefaults: (endpoint) => {
          const headers = {
            Accept: "application/json; charset=utf-8",
            Authorization: "Bearer " + localStorage.accessToken,
          };
          return new Promise(async (resolve, reject) => {
            return axios
              .get(endpoint, { headers })
              .then((response) => resolve(response))
              .catch((error) => {
                if (error && error.response && error.response.status === 404) {
                  addError(
                    "The contract could not be loaded. This could be because the contract is invalid or is pending distribution. Please contact customer service at (800) 763-0234.",
                  );
                } else if (error && error.response) {
                  handleResponse(error.response);
                }
                reject();
              });
          });
        },
        getBlob: (endpoint) => {
          const headers = {
            Accept: "application/json; charset=utf-8",
            Authorization: "Bearer " + localStorage.accessToken,
          };
          return new Promise(async (resolve, reject) => {
            return axios
              .get(endpoint, { responseType: "blob", headers: headers })
              .then((response) => resolve(response))
              .catch((error) => {
                if (error && error.response) {
                  return handleResponse(error.response);
                }
                reject();
              });
          });
        },
        delete: (endpoint) => {
          const headers = {
            Accept: "application/json; charset=utf-8",
            Authorization: "Bearer " + localStorage.accessToken,
          };
          return axios
            .delete(buildEncodedApiUrl(endpoint), { headers })
            .catch((error) => {
              if (error?.response) handleResponse(error.response);
              throw error;
            });
        },
        postPasswordReset: (endpoint, data) => {
          const headers = {
            "Content-Type": "application/json; charset=utf-8",
            Authorization: "Bearer " + localStorage.accessToken,
          };
          return new Promise(async (resolve, reject) => {
            return axios
              .post(buildEncodedApiUrl(endpoint), data, { headers })
              .then((response) => resolve(response))
              .catch((error) => {
                if (error && error.response) {
                  handleResponse(error.response);
                  reject(error.response);
                } else {
                  reject();
                }
              });
          });
        },
        post: (endpoint, data) => {
          const headers = {
            "Content-Type": "application/json; charset=utf-8",
            Authorization: "Bearer " + localStorage.accessToken,
          };
          return new Promise(async (resolve, reject) => {
            return axios
              .post(buildEncodedApiUrl(endpoint), data, { headers })
              .then((response) => resolve(response))
              .catch((error) => {
                if (error && error.response) {
                  handleResponse(error.response);
                }
                reject();
              });
          });
        },
        postForm: (endpoint, formData) => {
          const headers = {
            Authorization: "Bearer " + localStorage.accessToken,
          };
          return new Promise(async (resolve, reject) => {
            return axios
              .post(buildEncodedApiUrl(endpoint), formData, { headers })
              .then((response) => resolve(response))
              .catch((error) => {
                if (error && error.response) {
                  return handleResponse(error.response);
                }
                reject();
              });
          });
        },
        putForm: (endpoint, formData) => {
          const headers = {
            Authorization: "Bearer " + localStorage.accessToken,
          };
          return new Promise(async (resolve, reject) => {
            return axios
              .put(buildEncodedApiUrl(endpoint), formData, { headers })
              .then((response) => resolve(response))
              .catch((error) => {
                if (error && error.response) {
                  return handleResponse(error.response);
                }
                reject();
              });
          });
        },
        addError,
        addSuccess,
      };
    };

    render() {
      return (
        <AuthConsumer>
          {(authContext) => (
            <MessagesConsumer>
              {(messagesContext) => (
                <Component
                  {...this.props}
                  api={this.createApi(messagesContext, authContext)}
                  axios={axios}
                />
              )}
            </MessagesConsumer>
          )}
        </AuthConsumer>
      );
    }
  };
}
